import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');



// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const script = narration.match(/\S+/g) || [];

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : script.length / 2.5;

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const span = (end - start) / (index - from);

    for (let k = from; k < index; k += 1) mapped[k] = {

      start: start + (k - from) * span, end: start + (k - from + 1) * span, measured: false,

    };

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}



function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function buildBeats(narration, timeline, phrases, endPhrase) {

  const script = narration.match(/\S+/g) || [];

  const offsets = phrases.map(phrase => wordOffset(narration, phrase));

  const end = endPhrase ? wordOffset(narration, endPhrase) : script.length;

  return offsets.map((from, index) => ({

    start: timeline.words[from]?.start ?? Infinity,

    text: script.slice(from, offsets[index + 1] ?? end).join(' '),

    timings: timeline.words.slice(from, offsets[index + 1] ?? end).map(word => word.start),

  }));

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}



// Read the audio clock each frame, but render only when a word boundary,

// pause, seek or playback rate changes. Native CSS handles the motion.

function useLessonClock({ time = 0, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => timeline.words.map(word => word.start), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    controller.current.syncId += 1;

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}



const MotionClock = createContext({ playing: true, rate: 1, syncId: 0 });

function MotionScope({ clock, children }) {

  const value = useMemo(() => ({ playing: clock.playing, rate: clock.rate, syncId: clock.syncId }),

    [clock.playing, clock.rate, clock.syncId]);

  return <MotionClock.Provider value={value}>{children}</MotionClock.Provider>;

}



// Freeze delays at mount/cue/seek. Do not change animationDelay on every

// clock update: CSS runs the 380ms rise/fade smoothly between React renders.

function RevealText({ as: Element = 'span', text = '', segments, timings, start = 0, now = 0,

  delay = 0, complete = false, className = '' }) {

  const { playing, rate, syncId } = useContext(MotionClock);

  const pieces = segments || [{ text }];

  const ariaText = pieces.map(piece => piece.text).join(' ');

  const timingKey = timings?.join(',') ?? '';

  const origin = useMemo(() => now, [syncId, start, delay, complete, ariaText, timingKey, rate]);

  let wordIndex = 0;

  return <Element className={className} aria-label={ariaText}>

    {pieces.map((piece, pieceIndex) => <React.Fragment key={pieceIndex}>

      {pieceIndex > 0 ? ' ' : ''}

      <span className={piece.handwritten ? `training-handwritten ${piece.long ? 'is-long' : ''}` : 'training-plain'}>

        {piece.text.split(/\s+/).filter(Boolean).map((word, index, words) => {

          const indexInPhrase = wordIndex++;

          const revealAt = timings?.[indexInPhrase] ?? start + delay + indexInPhrase * .09;

          return <React.Fragment key={index}>

            <span key={syncId} className="training-reveal-word" aria-hidden="true" style={{

              animationDelay: `${complete ? -10 : (revealAt - origin) / rate}s`,

              animationDuration: `${.38 / rate}s`,

              animationPlayState: playing ? 'running' : 'paused',

            }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

          </React.Fragment>;

        })}

      </span>

    </React.Fragment>)}

  </Element>;

}



// Phrase changes overlap briefly, without blurring the entire slide.

function SceneTransition({ sceneKey, start = 0, now, instant = false, children }) {

  const { playing, rate, syncId } = useContext(MotionClock);

  const origin = useMemo(() => now, [sceneKey, start, syncId, rate]);

  const previous = useRef({ key: sceneKey, syncId, children });

  const [outgoing, setOutgoing] = useState(null);

  useLayoutEffect(() => {

    const old = previous.current;

    const changed = old.key !== sceneKey;

    previous.current = { key: sceneKey, syncId, children };

    if (instant || old.syncId !== syncId) { setOutgoing(null); return undefined; }

    if (!changed) return undefined;

    setOutgoing(old);

    const timer = window.setTimeout(() => setOutgoing(null), 450 / rate);

    return () => window.clearTimeout(timer);

  }, [sceneKey, instant, syncId, rate]);

  useLayoutEffect(() => { previous.current.children = children; });

  return <div className="training-transition-shell">

    {outgoing && !instant && <div key={`out-${outgoing.key}`} className="training-scene-layer is-exiting" aria-hidden="true">{outgoing.children}</div>}

    <div key={`in-${sceneKey}-${syncId}`} className="training-scene-layer is-entering" style={{

      animationDelay: `${instant ? -10 : (start - origin) / rate}s`,

      animationDuration: `${.45 / rate}s`, animationPlayState: playing ? 'running' : 'paused',

    }}>{children}</div>

  </div>;

}



const REVEAL_STYLES = `

.training-plain {display:inline;}

.training-reveal-word {display:inline-block;opacity:0;transform:translateY(.36em);animation:training-word-in 380ms cubic-bezier(.18,.84,.22,1) both;}

.training-handwritten {display:inline-block;font-family:'Ugly Handwriting',cursive;font-size:1.12em;font-weight:400;letter-spacing:-.012em;line-height:.9;}

.training-handwritten.is-long {display:block;width:fit-content;max-width:100%;margin:.12em auto 0;}

.training-transition-shell {position:absolute;inset:0;overflow:hidden;}

.training-scene-layer {position:absolute;inset:0;}

.training-scene-layer.is-entering {z-index:2;animation:training-scene-enter 450ms cubic-bezier(.16,1,.3,1) both;}

.training-scene-layer.is-exiting {z-index:1;pointer-events:none;animation:training-scene-exit 450ms cubic-bezier(.4,0,.2,1) both;}

@keyframes training-word-in {to {opacity:1;transform:translateY(0);}}

@keyframes training-scene-enter {from {opacity:0;transform:translate3d(20px,10px,0);}to {opacity:1;transform:translate3d(0,0,0);}}

@keyframes training-scene-exit {from {opacity:1;transform:translate3d(0,0,0);}to {opacity:0;transform:translate3d(-18px,-8px,0);}}

.is-seeking .training-scene-layer.is-exiting {display:none;}

@media(prefers-reduced-motion:reduce){

 .training-reveal-word {animation:none;opacity:1;transform:none;}

 .training-scene-layer {animation:none!important;opacity:1;transform:none;filter:none;}

 .training-scene-layer.is-exiting {display:none;}

}

`;





const NARRATION = "Welcome to Tribe’s host training program.\n\nBefore we begin, let’s outline the roadmap for this training.\n\nWe’ll start with some background on Tribe, including how our events are structured and your role as a host.\n\nThen, we’ll cover how to prepare for an event and what you need to know before it starts.\n\nAfter that, we’ll discuss how to lead an event and help guests connect.\n\nFinally, we’ll go over our expectations for hosts and cover practical details, like how payments work.";

const SECTIONS = [

  { id: 'background', title: 'Background', description: 'What Tribe is, how our events are structured, and your role as a host.', cue: 'We’ll start' },

  { id: 'preparation', title: 'Preparing for an event', description: 'How to prepare for an event and what to know before it starts.', cue: 'Then,' },

  { id: 'facilitation', title: 'Facilitation', description: 'How to lead an event and help guests connect.', cue: 'After that,' },

  { id: 'operations', title: 'Operations', description: 'Our expectations for hosts and how payments work.', cue: 'Finally,' },

];





export default function Welcome(props = {}) {

  const { duration = 0, wordTimings = EMPTY_TIMINGS, audioStatus, playbackId = 0 } = props;

  const timeline = useMemo(() => buildTimeline(NARRATION, wordTimings, duration), [wordTimings, duration]);

  const beats = useMemo(() => buildBeats(NARRATION, timeline, ['Welcome to', 'Before we begin', 'let’s outline'], 'We’ll start'), [timeline]);

  const sections = useMemo(() => SECTIONS.map(section => ({ ...section, start: cueTime(NARRATION, timeline, section.cue) })), [timeline]);

  const clock = useLessonClock(props, timeline);

  const { now, seeking } = clock;

  const finished = audioStatus === 'Complete' || now >= (timeline.words.at(-1)?.end ?? Infinity);

  const open = finished || now >= sections[0].start;

  const beatIndex = activeIndex(beats, now);

  const beat = beats[beatIndex];

  let current = -1;

  sections.forEach((section, index) => { if (now >= section.start) current = index; });

  return <MotionScope clock={clock}><div className="welcome-simple" aria-label={Welcome.title} data-timing-mode={timeline.mode}>

    <style>{HEADLINE_FONT_IMPORT + REVEAL_STYLES + STYLES + DESIGN_STYLES + ROADMAP_HEADING_STYLES + HEADLINE_FONT_STYLES + WELCOME_TIMELINE_STYLES}</style>

    <section className={`welcome-simple-canvas ${open ? 'is-roadmap' : ''} ${finished ? 'is-reviewing' : ''} ${seeking ? 'is-seeking' : ''}`}>

      <div className="welcome-simple-intro" aria-hidden={open}>

        <SceneTransition sceneKey={`${playbackId}-${beatIndex}`} start={beat.start} now={now} instant={seeking || finished}>

          <div className="welcome-caption-center"><RevealText as="p" className="welcome-simple-caption" text={beat.text} timings={beat.timings} start={beat.start} now={now} /></div>

        </SceneTransition>

      </div>

      <div className="welcome-simple-roadmap" aria-hidden={!open}>

        <div className="welcome-roadmap-timeline" style={{

          '--roadmap-progress': finished ? 1 : Math.max(0, (current + .5) / sections.length),

          '--roadmap-mobile-progress': finished ? 1 : Math.max(0, current / (sections.length - 1)),

        }}>

          <span className="welcome-roadmap-progress" aria-hidden="true" />

          <ol className="welcome-roadmap-stages" aria-label="Training roadmap">

            {sections.map((section, index) => {

              const reached = finished || current >= index;

              return <li key={section.id}

                className={`welcome-roadmap-stage ${index % 2 === 0 ? 'is-above' : 'is-below'} ${reached ? 'is-reached' : ''} ${!finished && current === index ? 'is-current' : ''}`}

                aria-current={!finished && current === index ? 'step' : undefined}>

                <span className="welcome-stage-stem" aria-hidden="true" />

                <span className="welcome-stage-dot" aria-hidden="true" />

                <span className="welcome-stage-node" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>

                <div className="welcome-stage-copy">

                  <RevealText as="h2" text={section.title} start={section.start} now={now} complete={finished} />

                  <RevealText as="p" text={section.description} start={section.start} now={now} delay={.42} complete={finished} />

                </div>

              </li>;

            })}

          </ol>

        </div>

      </div>

    </section>

  </div></MotionScope>;

}



const STYLES = `

.welcome-simple {width:100%;color:#111;background:#fff;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;}

.welcome-simple * {box-sizing:border-box;}

.welcome-simple .welcome-simple-canvas {position:relative;min-height:530px;overflow:hidden;background:#fff;}

.welcome-simple .welcome-simple-intro {position:absolute;inset:0;opacity:1;visibility:visible;transition:opacity 220ms ease,visibility 0ms linear;}

.welcome-simple .welcome-caption-center {position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:36px 52px;}

.welcome-simple .is-roadmap .welcome-simple-intro {opacity:0;visibility:hidden;transition:opacity 220ms ease,visibility 0ms linear 220ms;}

.welcome-simple .welcome-simple-caption {width:100%;max-width:1020px;margin:0;font-family:inherit;font-size:clamp(38px,5.4vw,70px);font-weight:750;line-height:1.12;letter-spacing:-.04em;text-align:center;}

.welcome-simple .welcome-simple-roadmap {width:calc(100% - 96px);max-width:1200px;margin:0 auto;padding:32px 0 46px;opacity:0;visibility:hidden;transition:opacity 450ms ease;}

.welcome-simple .is-roadmap .welcome-simple-roadmap {opacity:1;visibility:visible;}

.welcome-simple .welcome-simple-roadmap h1 {margin:0 0 64px;font-family:inherit;font-size:clamp(29px,3.25vw,43px);font-weight:750;line-height:1.12;letter-spacing:-.035em;}

.welcome-simple .welcome-simple-grid {display:grid;grid-template-columns:repeat(4,minmax(0,1fr));column-gap:32px;row-gap:48px;list-style:none;margin:0;padding:0;}

.welcome-simple .welcome-simple-grid li {min-width:0;opacity:.24;transition:opacity 420ms ease;}

.welcome-simple .welcome-simple-grid li.is-past {opacity:.52;}

.welcome-simple .welcome-simple-grid li.is-current,.welcome-simple .is-reviewing .welcome-simple-grid li {opacity:1;}

.welcome-simple .welcome-simple-number {display:flex;align-items:center;justify-content:center;width:30px;height:30px;margin-bottom:20px;border:1px solid #d8d8d8;border-radius:50%;font-family:inherit;font-size:15px;font-weight:450;line-height:1;}

.welcome-simple .welcome-simple-grid h2 {margin:0 0 12px;font-family:inherit;font-size:clamp(19px,1.9vw,23px);font-weight:650;line-height:1.25;letter-spacing:-.022em;}

.welcome-simple .welcome-simple-grid p {margin:0;max-width:360px;font-family:inherit;font-size:clamp(16px,1.45vw,19px);font-weight:400;line-height:1.55;letter-spacing:-.014em;}

.host-training .ht-lesson-layout:has(.welcome-simple) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.welcome-simple)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.welcome-simple) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:1100px){

 .welcome-simple .welcome-simple-grid {grid-template-columns:repeat(2,minmax(0,1fr));column-gap:36px;row-gap:42px;}

 .welcome-simple .welcome-simple-roadmap h1 {margin-bottom:44px;}

 .welcome-simple .welcome-simple-roadmap {width:calc(100% - 64px);}

}

@media(max-width:540px){

 .welcome-simple .welcome-simple-canvas {min-height:530px;}

 .welcome-simple .welcome-caption-center {padding:28px 24px;}

 .welcome-simple .welcome-simple-caption {font-size:clamp(32px,8.4vw,46px);}

 .welcome-simple .welcome-simple-roadmap {width:calc(100% - 40px);padding:28px 0 38px;}

 .welcome-simple .welcome-simple-roadmap h1 {font-size:28px;margin-bottom:38px;}

 .welcome-simple .welcome-simple-grid {column-gap:22px;row-gap:36px;}

 .welcome-simple .welcome-simple-grid h2 {font-size:18px;}

 .welcome-simple .welcome-simple-grid p {font-size:15px;line-height:1.5;}

 .welcome-simple .welcome-simple-number {width:27px;height:27px;font-size:13px;margin-bottom:16px;}

}

@media(prefers-reduced-motion:reduce){.welcome-simple .welcome-simple-intro,.welcome-simple .welcome-simple-roadmap,.welcome-simple .welcome-simple-grid li {transition:none;}}

.welcome-simple .is-seeking .welcome-simple-grid li {transition:none;}

`;



Object.assign(Welcome, {

  slide: 1, id: 'welcome', title: 'Welcome', section: 'Background',

  parts: [{ part: 0, narration: NARRATION }],

  summary: ['Background on Tribe.', 'Prepare for an event.', 'Lead and help guests connect.', 'Host expectations and payments.'],

});



const DESIGN_STYLES = `

.welcome-simple,.host-event-structure,.host-tribe-slide,.host-role-slide {

 --training-heading-size:clamp(29px,3.25vw,43px);

 --training-hero-size:clamp(38px,5.4vw,70px);

 font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;

 -webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;

}

.welcome-simple .welcome-simple-roadmap {max-width:1040px;padding-top:36px;}

.welcome-simple .welcome-simple-roadmap h1 {text-align:center;font-size:clamp(24px,2.55vw,32px);margin-bottom:72px;}

.welcome-simple .welcome-simple-caption,.host-event-structure .event-flow-opening-copy,.definition-caption h1,.tribe-concept-intro h1,.host-role-slide .role-heading {

 max-width:850px;font-size:clamp(32px,4vw,50px);line-height:1.12;letter-spacing:-.035em;

}

.welcome-simple .welcome-simple-number {width:26px;height:26px;font-size:11px;font-weight:500;font-variant-numeric:tabular-nums;}

.welcome-simple .welcome-simple-grid h2,.difference-field-item h2,.host-role-slide .role-column-heading h2 {font-size:clamp(18px,1.7vw,21px);line-height:1.3;}

.welcome-simple .welcome-simple-grid p,.difference-field-item p {font-size:clamp(13px,1.15vw,15px);line-height:1.55;}

.difference-field-title,.host-event-structure .event-flow-heading,.host-role-slide .is-open .role-heading {font-size:clamp(24px,2.55vw,32px);line-height:1.15;}

.difference-field {padding-top:36px;}

.difference-field-items {max-width:1040px;margin-top:80px;}

.difference-number,.host-role-slide .role-column-number {width:26px;height:26px;font-size:11px;}

.host-role-slide .role-column-number {flex-basis:26px;}

.host-role-slide .role-table {max-width:960px;}

.host-role-slide .role-table-cell p {font-size:clamp(18px,1.7vw,22px);}

.definition-review h1 {max-width:780px;font-size:clamp(27px,3vw,38px);}

.host-event-structure .event-flow-scene {min-height:530px;}

.host-event-structure .event-flow-heading {top:36px;}

.host-event-structure .journey-diagrams {max-width:1120px;margin-top:136px;}

.host-event-structure .journey-stage-label {font-size:21px;font-weight:650;}

.host-event-structure .journey-stage-number {font-size:30px;font-weight:500;}

@media(max-width:700px){

 .welcome-simple,.host-event-structure,.host-tribe-slide,.host-role-slide {

  --training-heading-size:clamp(27px,6.6vw,36px);--training-hero-size:clamp(32px,8.4vw,46px);

 }

 .host-event-structure .event-flow-scene {min-height:540px;}

 .welcome-simple .welcome-simple-caption,.host-event-structure .event-flow-opening-copy,.definition-caption h1,.tribe-concept-intro h1,.host-role-slide .role-heading {font-size:clamp(28px,7.6vw,38px);}

 .welcome-simple .welcome-simple-roadmap h1,.difference-field-title,.host-event-structure .event-flow-heading,.host-role-slide .is-open .role-heading {font-size:clamp(23px,5.8vw,29px);}

 .host-event-structure .event-flow-heading {top:28px;}

 .host-event-structure .journey-diagrams {max-width:460px;margin-top:112px;}

 .welcome-simple .welcome-simple-grid p,.difference-field-item p {font-size:14px;}

 .difference-field-items {margin-top:40px;}

 .host-role-slide .role-column-heading h2 {font-size:clamp(16px,4vw,19px);}

 .host-role-slide .role-table-cell p {font-size:clamp(16px,4vw,19px);}

}

`;



const ROADMAP_HEADING_STYLES = `

.welcome-simple .welcome-simple-roadmap h1.welcome-roadmap-heading {

 display:flex;align-items:baseline;flex-wrap:wrap;gap:0 .2em;

 width:fit-content;max-width:100%;margin:0 0 54px;

 font-size:22px;font-weight:550;line-height:1.4;letter-spacing:-.025em;text-align:left;

}

.welcome-simple .welcome-roadmap-heading .training-handwritten {

 margin-left:.05em;font-size:1.25em;line-height:.95;letter-spacing:0;

 transform:translateY(-.025em) rotate(-1.5deg);

}

.welcome-simple .welcome-roadmap-heading .training-handwritten .training-reveal-word {position:relative;}

.welcome-simple .welcome-roadmap-heading .training-handwritten .training-reveal-word::after {

 content:'';position:absolute;left:.025em;right:.025em;bottom:-.14em;height:1px;

 background:currentColor;border-radius:50%;transform:rotate(-1.5deg);opacity:.6;

}

@media(max-width:700px){

 .welcome-simple .welcome-simple-roadmap h1.welcome-roadmap-heading {font-size:20px;margin-bottom:42px;}

}

`;



const HEADLINE_FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700&display=swap');";

const HEADLINE_FONT_STYLES = `

.welcome-simple h1,

.welcome-simple .welcome-simple-caption,

.welcome-simple .welcome-simple-roadmap h1.welcome-roadmap-heading,

.host-tribe-slide h1,

.host-tribe-slide .difference-field-title,

.host-event-structure .event-flow-heading,

.host-event-structure .event-flow-opening-copy,

.host-role-slide .role-heading {

 font-family:'Merriweather',Georgia,'Times New Roman',serif;

 font-weight:700;font-style:normal;letter-spacing:-.015em;

}

.welcome-simple h1 .training-handwritten,

.welcome-simple .welcome-roadmap-heading .training-handwritten,

.host-tribe-slide h1 .training-handwritten,

.host-role-slide h1 .training-handwritten,

.host-event-structure h1 .training-handwritten {

 font-family:inherit;font-weight:inherit;font-size:inherit;line-height:inherit;

 font-style:normal;letter-spacing:inherit;transform:none;

}

`;



const WELCOME_TIMELINE_STYLES = `

.welcome-simple .welcome-simple-roadmap {max-width:1000px;padding-top:36px;padding-bottom:40px;}

.welcome-simple .welcome-simple-roadmap h1.welcome-roadmap-heading {margin-bottom:24px;font-size:22px;line-height:1.35;}

.welcome-simple .welcome-roadmap-timeline {position:relative;width:100%;margin:0 auto;}

.welcome-simple .welcome-roadmap-timeline::before,

.welcome-simple .welcome-roadmap-progress {position:absolute;z-index:0;top:170px;left:0;right:0;height:1px;background:#dedede;content:'';}

.welcome-simple .welcome-roadmap-progress {background:#111;transform:scaleX(var(--roadmap-progress));transform-origin:left center;transition:transform 600ms cubic-bezier(.18,.84,.22,1);}

.welcome-simple .welcome-roadmap-stages {position:relative;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));list-style:none;margin:0;padding:0;}

.welcome-simple .welcome-roadmap-stage {position:relative;min-width:0;height:350px;opacity:.24;transition:opacity 380ms ease;}

.welcome-simple .welcome-roadmap-stage.is-reached {opacity:.52;}

.welcome-simple .welcome-roadmap-stage.is-current,

.welcome-simple .is-reviewing .welcome-roadmap-stage {opacity:1;}

.welcome-simple .welcome-stage-stem {position:absolute;z-index:0;left:50%;width:1px;background:#dedede;}

.welcome-simple .is-above .welcome-stage-stem {top:124px;height:46px;}

.welcome-simple .is-below .welcome-stage-stem {top:170px;height:48px;}

.welcome-simple .welcome-stage-dot {position:absolute;z-index:2;top:170px;left:50%;width:12px;height:12px;transform:translate(-50%,-50%);border:1px solid #bcbcbc;border-radius:50%;background:#fff;transition:background 380ms ease,border-color 380ms ease;}

.welcome-simple .welcome-roadmap-stage.is-reached .welcome-stage-dot {border-color:#111;background:#111;}

.welcome-simple .welcome-stage-node {position:absolute;z-index:1;left:50%;display:grid;place-items:center;width:64px;height:64px;transform:translateX(-50%);border:1px solid #d5d5d5;border-radius:50%;color:#111;background:#fff;font-size:21px;font-weight:550;font-variant-numeric:tabular-nums;letter-spacing:-.025em;transition:color 380ms ease,background 380ms ease,border-color 380ms ease;}

.welcome-simple .is-above .welcome-stage-node {top:60px;}

.welcome-simple .is-below .welcome-stage-node {top:218px;}

.welcome-simple .welcome-roadmap-stage.is-reached .welcome-stage-node {border-color:#111;}

.welcome-simple .welcome-roadmap-stage.is-current .welcome-stage-node {color:#fff;background:#111;}

.welcome-simple .welcome-stage-copy {position:absolute;left:50%;width:calc(100% - 28px);max-width:196px;transform:translateX(-50%);text-align:center;}

.welcome-simple .is-above .welcome-stage-copy {top:204px;}

.welcome-simple .is-below .welcome-stage-copy {bottom:204px;}

.welcome-simple .welcome-stage-copy h2 {margin:0 0 9px;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:17px;font-weight:650;line-height:1.3;letter-spacing:-.02em;}

.welcome-simple .welcome-stage-copy p {margin:0;color:#737373;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;font-weight:400;line-height:1.5;letter-spacing:-.01em;}

.welcome-simple .is-seeking .welcome-roadmap-progress,

.welcome-simple .is-seeking .welcome-roadmap-stage,

.welcome-simple .is-seeking .welcome-stage-node,

.welcome-simple .is-seeking .welcome-stage-dot {transition:none;}

@media(max-width:720px){

 .welcome-simple .welcome-simple-roadmap {width:calc(100% - 40px);max-width:440px;padding-top:28px;}

 .welcome-simple .welcome-simple-roadmap h1.welcome-roadmap-heading {font-size:20px;margin-bottom:34px;}

 .welcome-simple .welcome-roadmap-stages {display:block;}

 .welcome-simple .welcome-roadmap-stage {height:140px;}

 .welcome-simple .welcome-roadmap-timeline::before,

 .welcome-simple .welcome-roadmap-progress {top:30px;bottom:110px;left:36px;right:auto;width:1px;height:auto;}

 .welcome-simple .welcome-roadmap-progress {transform:scaleY(var(--roadmap-mobile-progress));transform-origin:center top;}

 .welcome-simple .welcome-stage-stem,.welcome-simple .welcome-stage-dot {display:none;}

 .welcome-simple .is-above .welcome-stage-node,

 .welcome-simple .is-below .welcome-stage-node {top:6px;left:36px;width:48px;height:48px;font-size:18px;}

 .welcome-simple .welcome-stage-copy,

 .welcome-simple .is-above .welcome-stage-copy,

 .welcome-simple .is-below .welcome-stage-copy {top:6px;bottom:auto;left:84px;width:calc(100% - 84px);max-width:280px;transform:none;text-align:left;}

 .welcome-simple .welcome-stage-copy h2 {font-size:17px;}

 .welcome-simple .welcome-stage-copy p {font-size:13px;}

}

@media(prefers-reduced-motion:reduce){

 .welcome-simple .welcome-roadmap-progress,.welcome-simple .welcome-roadmap-stage,.welcome-simple .welcome-stage-node,.welcome-simple .welcome-stage-dot {transition:none;}

}

`;
